import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ShieldCheck, Lock, Server, Activity, Eye, EyeOff, Loader2 } from 'lucide-react'
import { useAuthStore } from '@/store/auth'
import { authService } from '@/services/firebase.service'
import toast from 'react-hot-toast'

export function Landing() {
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const navigate = useNavigate()
  const { setUser } = useAuthStore()

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email || !password) {
      toast.error('Inserisci email e password')
      return
    }

    setIsLoading(true)
    try {
      const firebaseUser = await authService.loginWithEmail(email, password)
      const userProfile = await authService.getUserProfile(firebaseUser.uid)

      if (userProfile) {
        setUser(userProfile)
      } else {
        // Create default profile if not exists
        setUser({
          id: firebaseUser.uid,
          email: firebaseUser.email || email,
          name: firebaseUser.displayName || email.split('@')[0],
          plan: 'free',
          minutesUsed: 0,
          minutesLimit: 60,
          voiceIdsUsed: 0,
          voiceIdsLimit: 3,
          createdAt: new Date().toISOString(),
        })
      }

      toast.success('Accesso sicuro effettuato.')
      navigate('/dashboard')
    } catch (error: any) {
      console.error('Login error:', error)
      if (error.code === 'auth/user-not-found') {
        toast.error('Utente non trovato. Registrati prima.')
      } else if (error.code === 'auth/wrong-password') {
        toast.error('Password errata.')
      } else if (error.code === 'auth/invalid-email') {
        toast.error('Email non valida.')
      } else {
        toast.error('Errore di accesso: ' + (error.message || 'Riprova'))
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleGoogleLogin = async () => {
    setIsLoading(true)
    try {
      const firebaseUser = await authService.loginWithGoogle()
      const userProfile = await authService.getUserProfile(firebaseUser.uid)

      if (userProfile) {
        setUser(userProfile)
      } else {
        setUser({
          id: firebaseUser.uid,
          email: firebaseUser.email || '',
          name: firebaseUser.displayName || 'Utente',
          avatar: firebaseUser.photoURL || undefined,
          plan: 'free',
          minutesUsed: 0,
          minutesLimit: 60,
          voiceIdsUsed: 0,
          voiceIdsLimit: 3,
          createdAt: new Date().toISOString(),
        })
      }

      toast.success('Accesso con Google effettuato.')
      navigate('/dashboard')
    } catch (error: any) {
      console.error('Google login error:', error)
      toast.error('Errore accesso Google: ' + (error.message || 'Riprova'))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex bg-slate-950 font-sans selection:bg-emerald-500/30">

      {/* LATO SINISTRO: Brand & Sicurezza (Impatto Visivo) */}
      <div className="hidden lg:flex lg:w-[55%] relative flex-col justify-between p-16 overflow-hidden">

        {/* Sfondo animato tecnico */}
        <div className="absolute inset-0 z-0">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-blue-900/20 via-slate-950 to-slate-950"></div>
            <div className="absolute top-0 left-0 w-full h-full bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20"></div>
            {/* Griglia animata */}
            <div className="absolute inset-0 bg-[linear-gradient(to_right,#1e293b_1px,transparent_1px),linear-gradient(to_bottom,#1e293b_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)] opacity-20"></div>
        </div>

        {/* Header Brand */}
        <div className="relative z-10 flex items-center gap-3">
            <div className="h-10 w-10 bg-white/10 backdrop-blur-md rounded-xl border border-white/20 flex items-center justify-center">
                <ShieldCheck className="h-6 w-6 text-white" />
            </div>
            <span className="text-xl font-bold text-white tracking-tight">NexScribe <span className="text-emerald-500">Enterprise Core</span></span>
        </div>

        {/* Main Copy */}
        <div className="relative z-10 max-w-2xl mt-auto mb-20">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
            >
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-950/30 border border-emerald-500/30 text-emerald-400 text-xs font-medium mb-6">
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                      <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500"></span>
                    </span>
                    System Operational • v2.4.0 Stable
                </div>
                <h1 className="text-5xl font-bold text-white leading-tight mb-6">
                    Intelligence Platform <br/>
                    <span className="text-slate-500">Authorized Access Only.</span>
                </h1>
                <p className="text-lg text-slate-400 leading-relaxed border-l-2 border-slate-700 pl-6">
                    Questa è un'istanza privata dedicata. Tutte le conversazioni sono crittografate end-to-end e processate in ambiente isolato (Single-Tenant).
                </p>
            </motion.div>
        </div>

        {/* Footer Tecnico */}
        <div className="relative z-10 flex items-center gap-8 text-xs text-slate-500 font-mono">
            <div className="flex items-center gap-2">
                <Server className="h-4 w-4" />
                <span>EU-WEST-1 (Milan)</span>
            </div>
            <div className="flex items-center gap-2">
                <Lock className="h-4 w-4" />
                <span>AES-256 GCM</span>
            </div>
            <div className="flex items-center gap-2">
                <Activity className="h-4 w-4" />
                <span>99.99% Uptime SLA</span>
            </div>
        </div>
      </div>

      {/* LATO DESTRO: Form Login Diretto */}
      <div className="w-full lg:w-[45%] bg-white flex flex-col justify-center items-center p-8 lg:p-16 relative">
         <div className="w-full max-w-md">

            {/* Header */}
            <div className="mb-8">
                <h2 className="text-2xl font-bold text-slate-900 tracking-tight">Accesso Workspace</h2>
                <p className="text-slate-500 mt-2 text-sm">Inserisci le credenziali aziendali per continuare.</p>
            </div>

            {/* Login Form */}
            <form onSubmit={handleLogin} className="space-y-5">
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Email Aziendale</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400/50 focus:border-slate-400 transition-all"
                  placeholder="nome@azienda.com"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 pr-12 text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400/50 focus:border-slate-400 transition-all"
                    placeholder="••••••••"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-slate-900 hover:bg-slate-800 text-white font-semibold py-3.5 rounded-xl flex items-center justify-center gap-2 transition-all shadow-xl shadow-slate-900/20 active:scale-[0.98] mt-2"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Verifica credenziali...
                  </>
                ) : (
                  <>
                    <Lock className="h-4 w-4" />
                    Accedi in Sicurezza
                  </>
                )}
              </button>
            </form>

            {/* Divider */}
            <div className="relative my-8">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200"></div></div>
                <div className="relative flex justify-center text-xs uppercase"><span className="bg-white px-3 text-slate-400 font-medium">Single Sign-On</span></div>
            </div>

            {/* SSO Buttons */}
            <div className="space-y-3">
                <button
                  type="button"
                  onClick={handleGoogleLogin}
                  disabled={isLoading}
                  className="w-full flex items-center justify-center gap-3 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 p-3.5 rounded-xl transition-all font-medium text-sm disabled:opacity-50"
                >
                   <img src="https://upload.wikimedia.org/wikipedia/commons/5/53/Google_%22G%22_Logo.svg" className="h-5 w-5" alt="Google" />
                   Accedi con Google Workspace
                </button>

                <button className="w-full flex items-center justify-center gap-3 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 p-3.5 rounded-xl transition-all font-medium text-sm">
                   <img src="https://upload.wikimedia.org/wikipedia/commons/4/44/Microsoft_logo.svg" className="h-5 w-5" alt="Microsoft" />
                   Accedi con Microsoft Entra ID
                </button>
            </div>

            {/* Footer Note */}
            <p className="mt-10 text-center text-xs text-slate-400">
                Non hai un account?{' '}
                <button
                  type="button"
                  onClick={() => navigate('/register')}
                  className="text-blue-600 hover:text-blue-700 font-medium"
                >
                  Registrati qui
                </button>
                <br/>
                <span className="text-slate-500">Gli accessi non autorizzati sono monitorati.</span>
            </p>
         </div>
      </div>
    </div>
  )
}
